'use client';
import React, { useState } from 'react';
import { Check, X, Lock, AlertTriangle } from 'lucide-react';

interface PermissionRequest {
  id: string;
  childName: string;
  childAvatar: string;
  courseName: string;
  wing: string;
  ageRange: string;
  requestedAt: string;
  status: 'pending' | 'approved' | 'denied';
}

const initialRequests: PermissionRequest[] = [
  { id: 'req-001', childName: 'Amara Osei', childAvatar: '🌟', courseName: 'Advanced Mathematics', wing: 'Advanced', ageRange: '14–17', requestedAt: '2h ago', status: 'pending' },
  { id: 'req-002', childName: 'Amara Osei', childAvatar: '🌟', courseName: 'AI Ethics & Safety', wing: 'Advanced', ageRange: '14–17', requestedAt: '1d ago', status: 'pending' },
  { id: 'req-003', childName: 'Kofi Osei', childAvatar: '🚀', courseName: 'Digital Citizenship', wing: 'Intermediate', ageRange: '10–13', requestedAt: '3d ago', status: 'pending' },
];

export default function ParentPermissionRequests() {
  const [requests, setRequests] = useState<PermissionRequest[]>(initialRequests);
  const [processing, setProcessing] = useState<string | null>(null);

  const handleDecision = async (id: string, decision: 'approved' | 'denied') => {
    setProcessing(id);
    // BACKEND INTEGRATION: POST /api/permissions/decide with requestId, decision, parentId
    await new Promise((r) => setTimeout(r, 600));
    setRequests((p) => p.map((r) => r.id === id ? { ...r, status: decision } : r));
    setProcessing(null);
  };

  const pending = requests.filter((r) => r.status === 'pending');
  const decided = requests.filter((r) => r.status !== 'pending');

  return (
    <div className="bg-card border border-border rounded-2xl p-5">
      <div className="flex items-center gap-2 mb-4">
        <h2 className="text-base font-bold text-foreground">Permission Requests</h2>
        {pending.length > 0 && (
          <span className="text-xs font-semibold bg-red-100 text-red-600 px-2 py-0.5 rounded-full border border-red-200">
            {pending.length} pending
          </span>
        )}
      </div>

      {pending.length === 0 && decided.length === 0 ? (
        <p className="text-sm text-muted-foreground text-center py-6">No pending permission requests.</p>
      ) : (
        <div className="space-y-3">
          {pending.map((req) => (
            <div key={req.id} className="p-3 rounded-xl border border-amber-200 bg-amber-50 space-y-2">
              <div className="flex items-start gap-2">
                <AlertTriangle size={14} className="text-amber-600 shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-semibold text-foreground">{req.childName}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    <Lock size={11} className="text-amber-600" />
                    <p className="text-xs text-amber-700 font-medium">{req.courseName}</p>
                    <span className="text-xs text-muted-foreground">· {req.wing} · Ages {req.ageRange}</span>
                  </div>
                  <p className="text-xs text-muted-foreground mt-0.5">Requested {req.requestedAt}</p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={() => handleDecision(req.id, 'approved')}
                  disabled={processing === req.id}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-emerald-500 text-white text-xs font-semibold rounded-lg btn-scale hover:bg-emerald-600 transition-colors disabled:opacity-50"
                >
                  {processing === req.id ? (
                    <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  ) : (
                    <Check size={13} />
                  )}
                  Approve
                </button>
                <button
                  onClick={() => handleDecision(req.id, 'denied')}
                  disabled={processing === req.id}
                  className="flex-1 flex items-center justify-center gap-1.5 py-2 bg-red-500 text-white text-xs font-semibold rounded-lg btn-scale hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  <X size={13} />
                  Deny
                </button>
              </div>
            </div>
          ))}

          {decided.map((req) => (
            <div key={req.id} className={`p-3 rounded-xl border text-sm flex items-center gap-3 ${req.status === 'approved' ? 'border-emerald-200 bg-emerald-50' : 'border-border bg-muted/40'}`}>
              {req.status === 'approved' ? (
                <Check size={14} className="text-emerald-600 shrink-0" />
              ) : (
                <X size={14} className="text-muted-foreground shrink-0" />
              )}
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate">{req.courseName}</p>
                <p className="text-xs text-muted-foreground">{req.childName} · {req.requestedAt}</p>
              </div>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${req.status === 'approved' ? 'bg-emerald-100 text-emerald-700' : 'bg-muted text-muted-foreground'}`}>
                {req.status.charAt(0).toUpperCase() + req.status.slice(1)}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}